<!-- # 지출결의서 리스트 화면 
          Q(partner_company__name__icontains=search) |  # 거래처명
                    Q(document_number__icontains=search) |  # 지출결의서번호
                    Q(full_name__icontains=search)  # 작성자 이름
 -->
<template>
    <div class="expense-list-view">
      <!-- 페이지 헤더: 타이틀 + 주 액션(추가), 보조 액션은 PC 버튼/모바일 드롭다운 -->
      <div class="page-header">
        <h2>지출결의서</h2>
        <div class="header-actions">
          <template v-if="!isMobile">
            <el-button type="success" plain :disabled="selectedCount === 0" @click="openBulkDialog">
              상태 일괄 변경{{ selectedCount ? ` (${selectedCount})` : '' }}
            </el-button>
            <el-button plain @click="$router.push('/expenses/vendor-settlement')">업체 정산</el-button>
            <el-button type="success" plain @click="openExportDialog">엑셀 다운로드</el-button>
            <el-button :type="showAll ? '' : 'warning'" plain @click="toggleShowAll">
              {{ showAll ? '스마트뷰' : '전체보기' }}
            </el-button>
            <el-button @click="showHelp">
              <el-icon><QuestionFilled /></el-icon>
              도움말
            </el-button>
          </template>
          <el-dropdown v-else trigger="click" @command="onMoreCommand">
            <el-button>
              <el-icon><MoreFilled /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="toggleShowAll">{{ showAll ? '스마트뷰로 보기' : '전체보기' }}</el-dropdown-item>
                <el-dropdown-item command="export">엑셀 다운로드</el-dropdown-item>
                <el-dropdown-item command="help" divided>도움말</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-button type="primary" @click="addExpense">추가</el-button>
        </div>
      </div>

      <!-- 검색/필터 -->
      <div class="filter-bar">
        <el-input
          v-model="searchQuery"
          class="filter-search"
          :placeholder="isMobile ? '거래처·지결번호·작성자 검색' : '검색어를 입력하세요(거래처명,지출결의서번호,작성자를 검색합니다.)'"
          clearable
          @input="applyFilters"
        />
        <el-select v-model="selectedYear" class="filter-year" placeholder="작성년도" @change="applyFilters">
          <el-option v-for="y in yearOptions" :key="y" :label="`${y}년`" :value="y" />
        </el-select>
        <el-select v-model="selectedStatus" class="filter-status" placeholder="상태" @change="applyFilters" clearable>
          <el-option value="incompleted" label="진행중"/>
          <el-option value="draft" label="작성중"/>
          <el-option value="submitted" label="제출완료"/>
          <el-option value="received" label="경리과접수"/>
          <el-option value="processing" label="집행중"/>
          <el-option value="completed" label="집행완료"/>
        </el-select>
      </div>

      <!-- 결과 요약: 현재 뷰 + 건수 + 합계 -->
      <div class="results-meta">
        <span class="view-badge" :class="{ 'view-badge--warn': !showAll && !selectedStatus }">{{ viewBadgeText }}</span>
        <span class="results-count">{{ rowData.length }}건</span>
        <span class="results-sum">합계 {{ formatCurrency(totalAmount) }}</span>
      </div>

      <!-- 모바일: dense 리스트 (탭하면 상세 이동) -->
      <div v-if="isMobile" class="mobile-expense-list">
        <div
          v-for="row in rowData"
          :key="row.id"
          class="expense-row"
          @click="router.push(`/expenses/${row.id}`)"
        >
          <div class="row-line1">
            <span class="partner">{{ row.partner_company_name || '-' }}</span>
            <span class="amount">{{ formatCurrency(row.amount) }}</span>
          </div>
          <div class="row-line2">
            <span class="doc-no">{{ row.document_number }}</span>
            <span class="due-date">지급 {{ row.payment_due_date || '-' }}</span>
            <span class="status-chip" :class="`chip-${row.status}`">{{ statusLabel(row.status) }}</span>
            <button
              v-if="row.is_creator && row.status === 'draft'"
              type="button"
              class="expense-cancel-btn"
              @click.stop="cancelExpense(row)"
            >취소</button>
          </div>
        </div>
        <el-empty v-if="rowData.length === 0" :image-size="60" description="조건에 맞는 지출결의서가 없습니다" />
      </div>

      <!-- PC: ag-grid -->
      <div v-else class="ag-grid-container">
        <!-- ag -grid -->
        <ag-grid-vue
          class="ag-theme-alpine"
          style="width: 100%; height: 100%;"
          :rowData="rowData"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :pagination="true"
          :paginationPageSize="pageSize"        
          :rowHeight="34"
          :headerHeight="36"          
          :rowSelection="'multiple'"
          :loadingOverlayComponent="'agLoadingOverlay'"
          @rowDoubleClicked="onRowClicked"
          @cellClicked="onCellClicked"
          @selection-changed="onSelectionChanged"
          domLayout="normal"
          @grid-ready="onGridReady"

        ></ag-grid-vue>
      </div>


      
      <!-- ✅ 선택 확인 및 상태 변경 팝업 -->
      <el-dialog v-model="dialogVisible" title="선택한 지출결의서 상태 변경">
        <el-table :data="selectedRows || []">
          <el-table-column prop="document_number" label="결의서 번호" />
          <el-table-column prop="partner_company_name" label="거래처" />
          <el-table-column label="금액">
              <template #default="{ row }">
                {{ formatCurrency(row.amount) }}
              </template>
          </el-table-column>
          <el-table-column label="현재 상태">
            <template #default="{ row }">
              {{ statusLabel(row.status) }}
            </template>
          </el-table-column>
        </el-table>

        <div style="margin-top: 16px;">
          <el-select v-model="newStatus" placeholder="변경할 상태">
            <el-option value="received" label="경리과 접수"/>
            <el-option value="processing" label="집행 중"/>
            <el-option value="completed" label="집행 완료"/>
          </el-select>
        </div>

        <template #footer>
          <el-button @click="dialogVisible = false">취소</el-button>
          <el-button type="primary" @click="confirmBulkUpdate">확인</el-button>
        </template>
      </el-dialog>

      <el-dialog v-model="exportDialog" title="지출결의서 엑셀 다운로드" width="420px">
        <div style="margin-bottom: 12px;">
          <el-select v-model="exportYear" placeholder="다운로드할 년도 선택" style="width: 100%;">
            <el-option v-for="y in yearOptions" :key="y" :label="`${y}년`" :value="y" />
          </el-select>
        </div>

        <template #footer>
          <el-button @click="exportDialog=false">취소</el-button>
          <el-button type="primary" :disabled="!exportYear" @click="downloadExcel">
            다운로드
          </el-button>
        </template>
      </el-dialog>

      <HelpPanel v-model="helpVisible" :content-path="'screens/expense-list'" />
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, onBeforeUnmount, onActivated } from "vue";
  import { useExpenseStore } from "@/stores/expenseStore";
  import { useRouter } from "vue-router";
  import "ag-grid-community/styles/ag-grid.css";
  import "ag-grid-community/styles/ag-theme-alpine.css";
  import axiosInstance from "@/api/axios";
  import {  ElMessage, ElMessageBox } from 'element-plus'; // 메시지 박스와 메시지 컴포넌트
  import HelpPanel from '@/components/help/HelpPanel.vue'
  import { QuestionFilled, MoreFilled } from '@element-plus/icons-vue'

  // 모바일 분기 (App.vue 등과 동일 기준 768px)
  const isMobile = ref(window.innerWidth <= 768);
  const onResize = () => { isMobile.value = window.innerWidth <= 768; };
  onMounted(() => window.addEventListener('resize', onResize));
  onBeforeUnmount(() => window.removeEventListener('resize', onResize));

  // 모바일 더보기(⋯) 메뉴 — 상태 일괄 변경은 다중선택이 필요해 PC 전용
  const onMoreCommand = (cmd) => {
    if (cmd === 'toggleShowAll') toggleShowAll();
    else if (cmd === 'export') openExportDialog();
    else if (cmd === 'help') showHelp();
  };

  //상태관리
  const searchQuery = ref("");
  const selectedStatus=ref("");
  const showAll = ref(false);
  const helpVisible = ref(false);
  const showHelp = () => { helpVisible.value = true }

  const dialogVisible = ref(false);
  const selectedRows = ref([]);
  const newStatus = ref("");

  const pageSize = ref(50)

  // 작성년도 필터 — 기본 현재년도, 최근 7년 선택지
  const currentYear = new Date().getFullYear();
  const selectedYear = ref(currentYear);
  const yearOptions = ref(Array.from({ length: 7 }, (_, i) => currentYear - i));


  //ag-grid 설정
  const columnDefs = ref([
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      pinned: 'left',
    },
    {
      field:"document_number",
      headerName:"지결번호",
      pinned: 'left', minWidth: 120, maxWidth: 160, flex: 0.8,
      // 단일 클릭 상세 진입 진입점 (onCellClicked 에서 라우팅)
      cellRenderer: (params) => `<span class="doc-link">${params.value ?? ''}</span>`,
    },
    {field:"partner_company_name",headerName:"거래처",pinned: 'left', minWidth: 140, maxWidth: 200, flex: 1.1  },
    {field:"payment_due_date",headerName:"지급 요청일",minWidth: 120, maxWidth: 150, flex: 0.9 },
    {field:"amount",
      headerName:"원화 금액",
      type: 'rightAligned',
      cellClass: 'num-cell',
      valueFormatter: (params) => {
            if (params.value != null) {
                return new Intl.NumberFormat('ko-KR', {
                    style: 'currency',
                    currency: 'KRW'
                }).format(params.value);
            }
            return '-';
        },
      minWidth: 120, maxWidth: 150, flex: 0.9
    },
    {field:"payment_method_name",headerName:"결제 조건",minWidth: 130, maxWidth: 160, flex: 0.9  },
    {field:"category_name",headerName:"비용 구분",minWidth: 110, maxWidth: 140, flex: 0.7},
    {field:"our_compay_name",headerName:"발주처",minWidth: 110, maxWidth: 150, flex: 0.8, },
    {
      headerName: "상태",
      field: "status",
      cellRenderer: (params) => {
        const label = statusLabel(params.value);
        return `<span class="status-chip chip-${params.value}">${label}</span>`;
      },
      minWidth: 110, maxWidth: 140, flex: 0.7
    },
    {field:"close_month",headerName:"마감월",minWidth: 110, maxWidth: 140, flex: 0.7},
    {field:"created_by_name",headerName:"작성자",minWidth: 110, maxWidth: 140, flex: 0.7},
    {field:"created_at",headerName:"작성일자",minWidth: 110, maxWidth: 140, flex: 0.7},
    {
      headerName: "취소",
      field: "_cancel",
      pinned: 'right',
      width: 80,
      filter: false,
      floatingFilter: false,
      sortable: false,
      cellRenderer: (params) => {
        // 본인이 작성한 draft 만 취소 버튼 노출 (is_creator 는 ExpenseSerializer 가 내려줌)
        if (params.data?.is_creator && params.data?.status === 'draft') {
          return `<button type="button" class="expense-cancel-btn">취소</button>`;
        }
        return '';
      },
    },

  ]);

  //그리드데이터
  const rowData = ref([]);

  const defaultColDef = {
    minWidth: 100, // ✅ 최소 너비 설정 (모바일에서도 텍스트가 잘리지 않도록)
    resizable: true,
    sortable: true,
    filter: true, // 필터 활성화 (필터 엔진)
    floatingFilter: true, // 헤더 아래 필터 입력 행 표시 — v32 Community 에서 메뉴 모듈 없이 필터 UI 진입점 확보
  };

  // gridApi 및 columnApi 관리
  const gridApi = ref(null);
  const columnApi = ref(null);

  // grid-ready 이벤트 핸들러
  const onGridReady = (params) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
  };

  // 선택 건수 — 상태 일괄 변경 버튼 활성화 조건
  const selectedCount = ref(0);
  const onSelectionChanged = () => {
    selectedCount.value = gridApi.value ? gridApi.value.getSelectedRows().length : 0;
  };

  // 결과 요약 (건수·합계·현재 뷰)
  const totalAmount = computed(() =>
    rowData.value.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
  );
  const viewBadgeText = computed(() => {
    const year = selectedYear.value ? `${selectedYear.value}년` : '전체 년도';
    let scope;
    if (selectedStatus.value === 'incompleted') scope = '진행중';
    else if (selectedStatus.value) scope = statusLabel(selectedStatus.value);
    else if (showAll.value) scope = '전체';
    else scope = '진행중';
    return `${year} · ${scope}`;
  });

  
  // 서버 조회 — 현재 화면 state(year/search/status/showAll) 에서 params 를 직접 조립.
  // 스마트뷰 룰:
  //   - selectedStatus === '' && !showAll  → exclude_status=completed (기본 진행중만)
  //   - selectedStatus === 'incompleted'   → exclude_status=completed (드롭다운 진행중 명시)
  //   - selectedStatus === <특정상태>      → 그 status 만
  //   - showAll === true && status 없음    → 필터 없음(전체)
  // 행(row) 접근(본인 부서 vs 전사)은 백엔드 get_queryset 이 자동 처리.
  const fetchExpenses = async () => {
    try {
      const params = {};

      if (selectedYear.value) params.year = selectedYear.value;
      if (searchQuery.value)  params.search = searchQuery.value;

      if (selectedStatus.value === 'incompleted') {
        // 드롭다운에서 "진행중" 명시 선택
        params.exclude_status = 'completed';
      } else if (selectedStatus.value) {
        params.status = selectedStatus.value;
      } else if (!showAll.value) {
        // 스마트뷰 기본: status 미지정 + 전체보기 아님 → 진행중만
        params.exclude_status = 'completed';
      }

      // 서버 페이지네이션 follow — StandardPagination page_size 가 바뀌어도 잘림 없이 전체 적재
      // (안전장치: 최대 20페이지)
      const all = [];
      let page = 1;
      while (page && page <= 20) {
        const response = await axiosInstance.get('expenses/', { params: { ...params, page } });
        const data = response.data;
        if (Array.isArray(data)) { all.push(...data); break; } // 페이지네이션 없는 응답 호환
        all.push(...(data.results || []));
        page = data.next ? page + 1 : 0;
      }
      rowData.value = all;
      selectedCount.value = 0; // 재조회 시 선택 초기화
    } catch (error) {
      console.error("Error fetching expenses", error);
    }
  };

  // 검색/년도/상태 변경 시 호출 — fetchExpenses 와 동일
  const applyFilters = fetchExpenses;

  const toggleShowAll = () => {
    showAll.value = !showAll.value;
    selectedStatus.value = '';
    fetchExpenses();
  };




   // 초기 데이터 로드 — 현재년도 + 스마트뷰(진행중) 기본
   const expenseStoreForNew = useExpenseStore();
   onMounted(fetchExpenses);
   // keep-alive 탭 복귀 시 재조회 (2026-11-03 제보 서효정) — 상세에서 접수/집행 처리 후
   // 목록 탭으로 돌아오면 캐시된 옛 목록이 보여 "접수가 안 된 것처럼" 보이던 문제.
   // 그 옛 행으로 일괄 상태 변경을 하면 꼬이므로 F5 없이도 최신 상태로 맞춘다.
   let activatedOnce = false; // 첫 activated 는 onMounted 직후라 건너뜀(중복 조회 방지)
   onActivated(() => { if (activatedOnce) fetchExpenses(); activatedOnce = true; });


  const router = useRouter();
  

  const onRowClicked = (event) => {
    console.log(event.data.id)
    router.push(`/expenses/${event.data.id}`);
  };

  // 취소 처리 — PC 그리드 셀 버튼 / 모바일 리스트 버튼 공용
  const cancelExpense = async (row) => {
    if (!row?.is_creator || row.status !== 'draft') return;

    try {
      await ElMessageBox.confirm(
        `[${row.document_number}] 지출결의서를 취소하시겠습니까?`,
        '지출결의서 취소',
        { confirmButtonText: '취소 처리', cancelButtonText: '닫기', type: 'warning' }
      );
    } catch {
      return; // 사용자가 취소 처리를 닫음
    }

    try {
      await axiosInstance.post(`expenses/${row.id}/cancel/`);
      ElMessage.success('지출결의서가 취소되었습니다.');
      await applyFilters();
    } catch (error) {
      const msg = error?.response?.data?.error || '취소 중 오류가 발생했습니다.';
      ElMessage.error(msg);
    }
  };

  // 그리드 셀 클릭 — 지결번호 링크(상세 진입) / 취소 버튼 처리. 행 더블클릭 진입도 병행 유지
  const onCellClicked = async (event) => {
    if (event.colDef?.field === 'document_number') {
      router.push(`/expenses/${event.data.id}`);
      return;
    }
    if (event.colDef?.field !== '_cancel') return;
    const targetEl = event.event?.target;
    if (!targetEl || !targetEl.classList.contains('expense-cancel-btn')) return;
    await cancelExpense(event.data);
  };
  
  const addExpense = () => {
    expenseStoreForNew.requestNewForm(); // 캐시된 신규 폼에 "빈 폼으로" 신호 (2026-11-03)
    router.push("/expenses/new"); // 추가 모드로 이동
  };


  //일괄상태변경 
  const openBulkDialog = () => {
    const rows = gridApi.value.getSelectedRows();
    if (rows.length === 0) {
      ElMessage.warning("선택된 결의서가 없습니다.");
      return;
    }
    selectedRows.value = rows;
    dialogVisible.value = true;
  };

  const confirmBulkUpdate = async () => {
    if (!newStatus.value) {
      ElMessage.warning("변경할 상태를 선택하세요.");
      return;
    }

    try {
      const ids = selectedRows.value.map(row => row.id);
      await axiosInstance.post('expenses/bulk-update-status/', {
        ids,
        status: newStatus.value,
      });
      ElMessage.success("상태가 성공적으로 변경되었습니다.");
      dialogVisible.value = false;
      await fetchExpenses();
    } catch (error) {
      console.error("상태 변경 실패:", error);
      ElMessage.error("상태 변경 중 오류가 발생했습니다.");
    }
  };
  // 상태 한글 변환용
  const statusLabel = (status) => {
    const statusMap = {
      draft: "작성 중",
      submitted: "제출 완료",
      received: "경리과 접수",
      processing: "집행 중",
      completed: "집행 완료",
    };
    return statusMap[status] || status;
  };

  // 금액 포맷
  const formatCurrency = (amount) => {
    if (amount == null) return "-";
    return new Intl.NumberFormat("ko-KR", {
      style: "currency",
      currency: "KRW",
    }).format(amount);
  };
  

  //////////엑셀다운로드처리
  const exportDialog = ref(false);
  // 엑셀 다운로드 년도는 별도 ref 로 관리 (리스트 필터 selectedYear 와 분리)
  const exportYear = ref(currentYear);

  function openExportDialog() {
    exportYear.value = currentYear;
    exportDialog.value = true;
  }

  async function downloadExcel() {
    const year = exportYear.value;

    const res = await axiosInstance.get(`expenses/export/`, {
      params: { year },
      responseType: "blob",
      // 인증이 JWT면 이미 axios interceptor에 Authorization 붙는 전제
    });

    // 파일 다운로드 처리
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expenses_${year}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    exportDialog.value = false;
  }
  //////////엑셀다운로드처리 끝
  </script>
  
  <style>
  /* ===== 페이지 헤더 ===== */
  .expense-list-view .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }
  .expense-list-view .page-header h2 {
    margin: 0;
    font-size: 20px;
  }
  .expense-list-view .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
  .expense-list-view .header-actions .el-button {
    margin-left: 0; /* el-button 기본 좌측 마진 제거 — gap 으로 통일 */
  }

  /* ===== 검색/필터 바 ===== */
  .expense-list-view .filter-bar {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
  }
  .expense-list-view .filter-search { flex: 1; min-width: 0; }
  .expense-list-view .filter-year   { width: 110px; flex-shrink: 0; }
  .expense-list-view .filter-status { width: 130px; flex-shrink: 0; }
  @media (max-width: 768px) {
    .expense-list-view .filter-year   { width: 92px; }
    .expense-list-view .filter-status { width: 104px; }
  }

  /* ===== 모바일 dense 리스트 ===== */
  .mobile-expense-list {
    border-top: 1px solid #ebeef5;
  }
  .expense-row {
    padding: 9px 2px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
  }
  .expense-row:active { background: #f5f7fa; }
  .row-line1 {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
  }
  .row-line1 .partner {
    font-weight: 600;
    font-size: 14px;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-line1 .amount {
    font-weight: 700;
    font-size: 14px;
    color: #303133;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }
  .row-line2 {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 3px;
    font-size: 12px;
    color: #909399;
  }
  .row-line2 .doc-no { font-variant-numeric: tabular-nums; }
  .row-line2 .due-date {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .row-line2 .status-chip {
    margin-left: auto;
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 9px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.6;
  }
  .chip-draft      { background: #f4f4f5; color: #909399; }
  .chip-submitted  { background: #ecf5ff; color: #409eff; }
  .chip-received   { background: #fdf6ec; color: #e6a23c; }
  .chip-processing { background: #f0f9eb; color: #67c23a; }
  .chip-completed  { background: #303133; color: #fff; }

  /* ===== 결과 요약 줄 ===== */
  .expense-list-view .results-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    font-size: 12px;
    color: #606266;
  }
  .expense-list-view .view-badge {
    padding: 1px 8px;
    border-radius: 9px;
    background: #ecf5ff;
    color: #409eff;
    font-weight: 600;
  }
  .expense-list-view .view-badge--warn {
    background: #fdf6ec;
    color: #e6a23c;
  }
  .expense-list-view .results-sum {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* ===== ag-grid 내부 표현 (이 화면 한정) ===== */
  .expense-list-view .ag-theme-alpine .doc-link {
    color: #409eff;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
    font-variant-numeric: tabular-nums;
  }
  .expense-list-view .ag-theme-alpine .num-cell {
    font-variant-numeric: tabular-nums;
  }
  .expense-list-view .ag-theme-alpine .status-chip {
    padding: 1px 7px;
    border-radius: 9px;
    font-size: 11px;
    font-weight: 600;
  }

  /* ===== 이중 스크롤 방지 =====
     .content(전역 스크롤 컨테이너)의 가시 영역에 화면 전체를 flex 로 맞추고,
     그리드가 잔여 공간(flex:1)을 정확히 채움 → 바깥 스크롤 없이 그리드 스크롤만 발생.
     (기존 calc(100vh - 220px) 고정 높이는 상단 요소 높이와 어긋나 이중 스크롤 유발) */
  @media (min-width: 769px) {
    .expense-list-view {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .expense-list-view .page-header,
    .expense-list-view .filter-bar,
    .expense-list-view .results-meta {
      flex-shrink: 0;
    }
  }

  .expense-list-view .ag-grid-container {
    width: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
  }

  /* 작성중 지출결의서 취소 버튼 (ag-grid cellRenderer 안의 raw button) */
  .expense-cancel-btn {
    background: #f56c6c;
    color: #fff;
    border: 1px solid #f56c6c;
    border-radius: 4px;
    padding: 2px 10px;
    cursor: pointer;
    font-size: 12px;
    line-height: 1.4;
  }
  .expense-cancel-btn:hover {
    background: #f78989;
    border-color: #f78989;
  }
  </style>